import findStep from "./findStep";
import useBotControlStore from "../../../storeModules/useBotControlStore";
import useUserStore from "../../../storeModules/useUserStore";
import useConsorcioStore from "../../../storeModules/useConsorcioStore";
import {
  tagSimularConsorcio,
  tagRenda,
  tagRendaExtra,
} from "../../../utils/sendTag";
import TypesOfPlans from "../../../components/types-of-plans/types-of-plans";
import {
  getLead,
  getPlanCategoria,
  validateForbiddenWords,
  getRanges,
} from "../../../api/requests";
import ServicesPlans from "../../../components/servicesPlans/index";
import {
  setLoadingMessage,
  removeLoadingMessage,
} from "../../../utils/setLoadingMessage";
import noConnectionHandler from "../../../utils/noConnectionHandler";
import getFilteredOptions from "../../../components/servicesCarousel/getFilteredOptions";
import {
  CategoriasEnum,
  categoryNames,
  rendaExtraOptions,
} from "../../../utils/constants";
import useInitialConfigStore from "../../../storeModules/useInitialConfigStore";
import getCategoryName from "../../../utils/getCategoryName";
import trackConversacaoStep from "../../../utils/trackConversacaoStep";
import { persistCreditRanges } from "../../../utils/resumeStorage";

const isSimulateAgain = () => {
  return useUserStore.getState().isSimulateAgain;
};

const handleValidateForbiddenWordsName = async () => {
  return await noConnectionHandler().then(() => {
    return validateForbiddenWords(
      useUserStore.getState().basic.nome,
      "Por favor, insira um nome válido.",
      "saudacao_nome",
      "userBasicName",
    );
  });
};

const handldeValidateForbiddenWordsEmail = async () => {
  return await noConnectionHandler().then(() => {
    return validateForbiddenWords(
      useUserStore.getState().basic.email,
      "Por favor, insira um e-mail válido.",
      "pergunta_celular",
      "userEmail",
    );
  });
};

const handleGetLead = async () => {
  await noConnectionHandler().then(() => {
    getLead();
  });
};

const buildCategoryCarouselOptions = (categories) => {
  const { tipoTabela } = useInitialConfigStore.getState();

  const mappedCategorias = categories.map((categoria) => {
    const categoriaInfo = CategoriasEnum[categoria.categoriaID] || {};

    return {
      categoriaID: categoria.categoriaID,
      text: categoriaInfo.title,
      description: categoriaInfo.description,
      imagemUrl: categoria.imagemUrl,
      produtoCategoria: categoriaInfo.produtoCategoria,

      action: () => {
        const consorcioData = useConsorcioStore.getState();
        useConsorcioStore.setState({
          ...consorcioData,
          tipoBem: categoryNames[categoria.categoriaID],
          categoriaId: categoria.categoriaID,
          tipoPlano: categoriaInfo.tipoPlano,
        });

        useBotControlStore.setState({
          currentStep: findStep("pergunta_nome").index,
        });
      },
    };
  });

  return getFilteredOptions(mappedCategorias, tipoTabela);
};

export const loadFilteredCategories = async ({ navigate = true } = {}) => {
  const categories = await noConnectionHandler().then(() => getPlanCategoria());

  if (!categories?.length) return [];

  const filteredOptions = buildCategoryCarouselOptions(categories);

  useConsorcioStore.setState((state) => ({
    ...state,
    filteredCategories: filteredOptions,
  }));

  if (filteredOptions.length === 1) {
    filteredOptions[0].action();
  } else if (navigate) {
    useBotControlStore.setState({
      currentStep: findStep("escolha_categoria").index,
    });
  }

  return filteredOptions;
};

const leadSteps = [
  {
    name: "carrega_categorias",
    sender: "bot",
    text: null,
    isWaitingForFetch: true,
    action: async () => {
      await loadFilteredCategories({ navigate: true });
    },
  },
  {
    name: "escolha_categoria",
    sender: "bot",
    text: "Agora escolha o que <b>você deseja conquistar</b>:",
    isWaitingForFetch: true,
    useCarousel: true,
    action: async () => {
      const { filteredCategories } = useConsorcioStore.getState();
      if (filteredCategories?.length > 0) return;

      await loadFilteredCategories({ navigate: false });
    },
  },
  {
    name: "pergunta_nome",
    isWaitingForFetch: true,
    sender: "bot",
    text: "Como prefere que eu te chame?",
    inputType: "userBasicName",
    action: () => {
      return handleValidateForbiddenWordsName();
    },
  },
  {
    name: "saudacao_nome",
    sender: "bot",
    text: () => {
      const userBasicName = useUserStore.getState().basic.nome;
      return `Muito prazer, <b>${userBasicName} :)</b>`;
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("introducao_simulacao").index,
      });
    },
  },
  {
    name: "escolha_tipo_plano",
    sender: "bot",
    text: "Escolha uma das opções:",
    options: TypesOfPlans(),
  },
  {
    name: "introducao_simulacao",
    sender: "bot",
    text: "Vou te guiar nesse caminho para sua conquista.",
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("pergunta_valor_credito").index,
      });
    },
  },
  {
    name: "pergunta_valor_credito",
    sender: "bot",
    text: () => {
      const categoryName = getCategoryName()?.toLowerCase();
      const categoryNameMessage = categoryName ? `de ${categoryName}` : "";
      return `Qual é o <b>valor aproximado do crédito ${categoryNameMessage}</b> que você precisa?`;
    },
    action: async () => {
      await noConnectionHandler().then(() => {
        setLoadingMessage();

        getRanges()
          .then((response) => {
            const ranges = {
              minBemValor: response.minBemValor,
              maxBemValor: response.maxBemValor,
            };

            useConsorcioStore.setState((state) => ({ ...state, ranges }));

            persistCreditRanges(ranges);

            useBotControlStore.setState({
              currentStep: findStep("informa_valor_credito").index,
            });
          })
          .catch(() => {
            useBotControlStore.setState({
              currentStep: findStep("erro_geral").index,
            });
          })
          .finally(() => {
            removeLoadingMessage();
          });
      });
    },
  },
  {
    name: "informa_valor_credito",
    sender: "bot",
    inputType: "userCreditValue",
    isWaitingForFetch: true,
    text: () => {
      const { ranges } = useConsorcioStore.getState();
      return `Digite um valor entre:<br><br> <b>R$ ${ranges.minBemValor.toLocaleString(
        "pt-BR",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
      )} a R$ ${ranges.maxBemValor.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}</b>`;
    },
    action: async () => {
      useBotControlStore.setState({
        currentStep: findStep("mensagem_lista_planos").index,
      });
    },
  },
  {
    name: "mensagem_lista_planos",
    sender: "bot",
    text: "Certo, vou te mostrar planos que caibam no seu bolso e que não comprometam mais do que 30% da sua renda.",
    action: async () => {
      tagSimularConsorcio();

      useBotControlStore.setState({
        currentStep: findStep("planos_disponiveis").index,
      });
    },
  },
  {
    name: "planos_disponiveis",
    sender: "bot",
    text: () => {
      return isSimulateAgain()
        ? null
        : "Para isso, informe a sua <b>renda mensal</b>, incluindo <b>renda extra</b> e <b>familiar</b>, se houver.";
    },
    action: () => {
      const nextStep = isSimulateAgain()
        ? findStep("carrega_planos").index
        : findStep("pergunta_valor_renda").index;

      useBotControlStore.setState({
        currentStep: nextStep,
      });
    },
  },
  {
    name: "pergunta_valor_renda",
    sender: "bot",
    text: "Digite o <b>valor da renda</b>:",
    inputType: "userRendaMensal",
    action: () => {
      tagRenda();

      useBotControlStore.setState({
        currentStep: findStep("mensagem_email").index,
      });
    },
  },
  {
    name: "sem_renda_extra",
    sender: "bot",
    text: () => `Então você precisa escolher um <b>crédito menor.</b>`,
    inputType: "userCreditValue",
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("carrega_planos").index,
      });

      tagRendaExtra(rendaExtraOptions.NAO_TENHO);
    },
  },
  {
    name: "pergunta_renda_extra",
    sender: "bot",
    text: () => `Informe a <b>renda extra</b> ou <b>familiar</b>:`,
    inputType: "userRendaExtra",
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("renda_extra_confirmada").index,
      });

      tagRendaExtra(rendaExtraOptions.TENHO);
    },
  },
  {
    name: "renda_extra_confirmada",
    sender: "bot",
    text: () =>
      `Ótimo 😀! Agora vou somar as rendas para encontrar a <b>parcela ideal</b>, tá bem?`,
    options: [
      {
        text: "Tá bem :)",
        action: () => {
          const botMessages = useBotControlStore.getState().messages;

          const userData = useUserStore.getState();
          const userRendaExtra = useUserStore.getState().basic.rendaExtra;
          const userRendaMensal = useUserStore.getState().basic.rendaMensal;

          useUserStore.setState({
            ...userData,
            basic: {
              ...userData.basic,
              rendaMensal: userRendaExtra + userRendaMensal,
            },
          });

          const newMessage = {
            sender: "bot",
            text: "Vou te mostrar planos com <b>parcelas que cabem no seu bolso</b> e que não comprometem mais de <b>30%</b> da sua renda 😉.",
          };

          trackConversacaoStep({
            stepName: "renda_extra_confirmada",
            botMessage: newMessage.text,
          });

          useBotControlStore.setState({
            messages: [...botMessages, newMessage],
          });

          useBotControlStore.setState({
            currentStep: findStep("carrega_planos").index,
          });
        },
      },
    ],
  },
  {
    name: "mensagem_email",
    sender: "bot",
    text: () => {
      const userBasicName = useUserStore.getState().basic.nome;
      return `Legal, <b>${userBasicName}</b>! Enquanto procuro os <b>melhores planos</b> pra você, me diz:`;
    },
    action: async () => {
      useBotControlStore.setState({
        currentStep: findStep("pergunta_email").index,
      });
    },
  },
  {
    name: "pergunta_email",
    sender: "bot",
    text: "Qual o seu melhor <b>e-mail</b>?",
    inputType: "userEmail",
    action: async () => {
      return handldeValidateForbiddenWordsEmail();
    },
  },
  {
    name: "pergunta_celular",
    sender: "bot",
    text: "E qual o número do seu <b>celular</b> com DDD?",
    inputType: "userTelefone",
    action: async () => {
      useBotControlStore.setState({
        currentStep: findStep("registra_lead").index,
      });
    },
  },
  {
    name: "registra_lead",
    sender: "bot",
    text: "",
    action: async () => {
      handleGetLead();
    },
  },
  {
    name: "tabela_servicos_completa",
    sender: "bot",
    text: "",
    component: null,
    action: () => {
      const botMessages = useBotControlStore.getState().messages;

      const serviceMessage = {
        sender: "bot",
        text: `Opa, vamos partir pra essa conquista. Separei <b>dois planos que tenho disponíveis para você escolher:</b>`,
      };

      const planoIntegralMessage = {
        sender: "bot",
        text: `<b>Plano Integral</b><br>Pague 100% do valor das suas parcelas até o final do prazo contratado em seu plano.`,
      };

      const { skipStepTracking } = useBotControlStore.getState();
      if (!skipStepTracking) {
        trackConversacaoStep({
          stepName: "tabela_servicos_completa",
          botMessage: [serviceMessage.text, planoIntegralMessage.text],
        });
      }

      useBotControlStore.setState({
        messages: [...botMessages, serviceMessage, planoIntegralMessage],
      });
    },
    options: ServicesPlans(),
  },
];

export default leadSteps;
